/**
 * @file React component — DataTable.
 */

import React, { useMemo, useState } from "react";
import "./DataTable.css";

/**
 * Generic column definition for DataTable
 */
export interface Column<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  minWidth?: string;
  /** Cell and header text alignment (default left). */
  align?: "left" | "center" | "right";
}

/**
 * Filter option for dropdown filters
 */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Filter configuration
 */
export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  defaultValue?: string;
}

interface DataTableProps<T> {
  /** Array of data items to display */
  data: T[];
  /** Column definitions */
  columns: Column<T>[];
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Title of the table */
  title?: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Search function - if not provided, uses default text search on all columns */
  onSearch?: (searchTerm: string) => void;
  /** Search value - controlled component */
  searchValue?: string;
  /** Filter configurations */
  filters?: FilterConfig[];
  /** Filter values - controlled component */
  filterValues?: Record<string, string>;
  /** Callback when filters change */
  onFilterChange?: (key: string, value: string) => void;
  /** Enable pagination */
  pagination?: boolean;
  /** Items per page */
  itemsPerPage?: number;
  /** Callback when items per page changes */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  /** Custom row actions */
  renderActions?: (item: T) => React.ReactNode;
  /** Custom header actions (buttons, etc.) */
  headerActions?: React.ReactNode;
  /** Row click handler */
  onRowClick?: (item: T) => void;
  /** Custom empty state */
  renderEmpty?: () => React.ReactNode;
  /**
   * When true, renders without the full-page container padding/background.
   * Useful for embedding inside cards/pages that already provide layout.
   */
  embedded?: boolean;
  /**
   * When true, keeps the table header fixed while the table body scrolls.
   * Use together with wrapperMaxHeight to enable scrolling.
   */
  stickyHeader?: boolean;
  /**
   * Max height for the table wrapper. Enables scrolling when content exceeds it.
   * Example: 360 (px) or "360px".
   */
  wrapperMaxHeight?: number | string;
}

/**
 * Reusable DataTable component with search, filters, and pagination
 * Can be used across different sections of the application
 */
function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = "No data available",
  title,
  subtitle,
  searchPlaceholder = "Search...",
  onSearch,
  searchValue: controlledSearchValue,
  filters = [],
  filterValues: controlledFilterValues,
  onFilterChange,
  pagination = true,
  itemsPerPage: controlledItemsPerPage = 10,
  onItemsPerPageChange,
  renderActions,
  headerActions,
  onRowClick,
  renderEmpty,
  embedded = false,
  stickyHeader = false,
  wrapperMaxHeight,
}: DataTableProps<T>) {
  // Internal state for uncontrolled mode
  const [internalSearch, setInternalSearch] = useState("");
  const [internalFilters, setInternalFilters] = useState<Record<string, string>>(
    filters.reduce((acc, filter) => {
      acc[filter.key] = filter.defaultValue || "";
      return acc;
    }, {} as Record<string, string>)
  );
  const [internalItemsPerPage, setInternalItemsPerPage] = useState(10);

  // Use controlled or uncontrolled values
  const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearch;
  const filterValues = controlledFilterValues !== undefined ? controlledFilterValues : internalFilters;
  const itemsPerPage = onItemsPerPageChange ? controlledItemsPerPage : internalItemsPerPage;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Handle search
  const handleSearchChange = (value: string) => {
    if (onSearch) {
      onSearch(value);
    } else {
      setInternalSearch(value);
    }
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: string) => {
    if (onFilterChange) {
      onFilterChange(key, value);
    } else {
      setInternalFilters((prev) => ({ ...prev, [key]: value }));
    }
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search (if not using custom search handler)
    if (!onSearch && searchValue) {
      const searchLower = searchValue.toLowerCase();
      result = result.filter((item) =>
        columns.some((col) => {
          const value = item[col.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    // Apply filters
    filters.forEach((filter) => {
      const filterValue = filterValues[filter.key];
      if (filterValue && filterValue !== "") {
        result = result.filter((item) => {
          const itemValue = item[filter.key];
          return String(itemValue) === filterValue;
        });
      }
    });

    return result;
  }, [data, searchValue, filterValues, filters, columns, onSearch]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = pagination
    ? filteredData.slice(startIndex, endIndex)
    : filteredData;

  // Reset to page 1 if current page is out of bounds
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const wrapperStyle: React.CSSProperties | undefined =
    wrapperMaxHeight !== undefined
      ? {
          maxHeight:
            typeof wrapperMaxHeight === "number" ? `${wrapperMaxHeight}px` : wrapperMaxHeight,
        }
      : undefined;

  return (
    <div className={`data-table-container ${embedded ? "data-table-container--embedded" : ""}`}>
      {/* Header with Title and Search in single row */}
      {(title || subtitle || headerActions || searchPlaceholder) && (
        <div className="data-table-header-row">
          {/* Left: Title and Subtitle */}
          <div className="data-table-header-left">
            {title && <h2 className="data-table-title">{title}</h2>}
            {subtitle && <p className="data-table-subtitle">{subtitle}</p>}
            {headerActions && <div className="data-table-header-actions">{headerActions}</div>}
          </div>

          {/* Right: Search and Filters */}
          <div className="data-table-header-right">
            {/* Search Bar */}
            {searchPlaceholder && (
              <div className="data-table-search">
                <svg
                  className="search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 14L11.1 11.1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="data-table-search-input"
                />
              </div>
            )}

            {/* Filters */}
            {filters.length > 0 && (
              <div className="data-table-filters">
                {filters.map((filter) => (
                  <select
                    key={filter.key}
                    className="data-table-filter-select"
                    value={filterValues[filter.key] || ""}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  >
                    <option value="">All {filter.label}</option>
                    {filter.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        className={`data-table-wrapper ${stickyHeader ? "data-table-wrapper--sticky" : ""} ${
          wrapperMaxHeight !== undefined ? "data-table-wrapper--scroll" : ""
        }`}
        style={wrapperStyle}
      >
        {loading ? (
          <div className="data-table-loading">
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          renderEmpty ? (
            renderEmpty()
          ) : (
            <div className="data-table-empty">
              <p>{emptyMessage}</p>
            </div>
          )
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      textAlign: column.align ?? "left",
                    }}
                  >
                    {column.label}
                  </th>
                ))}
                {renderActions && <th className="actions-column">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr
                  key={item.id || index}
                  onClick={() => onRowClick?.(item)}
                  className={onRowClick ? "clickable-row" : ""}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{
                        textAlign: column.align ?? "left",
                        minWidth: column.minWidth,
                      }}
                    >
                      {column.render
                        ? column.render(item)
                        : item[column.key] ?? "-"}
                    </td>
                  ))}
                  {renderActions && (
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      {renderActions(item)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && filteredData.length > 0 && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            <span>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of{" "}
              {filteredData.length} entries
            </span>
            <div className="pagination-rows-select">
              <label>Rows per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  setCurrentPage(1);
                  if (onItemsPerPageChange) {
                    onItemsPerPageChange(newValue);
                  } else {
                    setInternalItemsPerPage(newValue);
                  }
                }}
                className="rows-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="pagination-numbers">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`pagination-number ${
                      currentPage === pageNum ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** React component — Data Table. */
export default DataTable;

